import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useEffect, useRef, useState } from 'react'

interface Slide {
    className: string
}

interface Breakpoints {
    xs?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
}

interface CarouselProps {
    slides: Slide[]
    visibleCount?: number
    breakpoints?: Breakpoints
    cardHeight?: string | number  // ← renamed from cardWidth
    gap?: number
    autoPlay?: boolean
    peekPx?: number
}

function Carousel({
    slides,
    visibleCount = 1,
    breakpoints,
    cardHeight = 300,             // ← renamed from cardWidth
    gap = 0,
    autoPlay = false,
    peekPx = 0
}: CarouselProps) {
    const theme = useTheme()

    const isXl = useMediaQuery(theme.breakpoints.up('xl'))
    const isLg = useMediaQuery(theme.breakpoints.up('lg'))
    const isMd = useMediaQuery(theme.breakpoints.up('md'))
    const isSm = useMediaQuery(theme.breakpoints.up('sm'))

    const resolvedCount = breakpoints
        ? isXl && breakpoints.xl != null ? breakpoints.xl
            : isLg && breakpoints.lg != null ? breakpoints.lg
                : isMd && breakpoints.md != null ? breakpoints.md
                    : isSm && breakpoints.sm != null ? breakpoints.sm
                        : breakpoints.xs != null ? breakpoints.xs
                            : visibleCount
        : visibleCount

    const [currentSlide, setCurrentSlide] = useState(0)
    const [isTransitioning, setIsTransitioning] = useState(true)
    const containerRef = useRef<HTMLDivElement>(null)
    const [cardPx, setCardPx] = useState(0)
    const dragStartX = useRef<number>(0)
    const dragDeltaX = useRef<number>(0)
    const isDragging = useRef<boolean>(false)
    const [liveOffset, setLiveOffset] = useState(0)

    const extendedSlides = [...slides, ...slides.slice(0, resolvedCount)]
    const totalSlides = slides.length

    useEffect(() => {
        const calculate = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth
                const totalGap = gap * (resolvedCount - 1)
                setCardPx((containerWidth - totalGap - peekPx) / resolvedCount)
            }
        }
        calculate()
        window.addEventListener('resize', calculate)
        return () => window.removeEventListener('resize', calculate)
    }, [resolvedCount, gap, peekPx])

    useEffect(() => {
        setIsTransitioning(false)
        setCurrentSlide(0)
    }, [resolvedCount])

    useEffect(() => {
        if (!autoPlay) return
        const interval = setInterval(() => {
            setIsTransitioning(true)
            setCurrentSlide((prev) => prev + 1)
        }, 3000)
        return () => clearInterval(interval)
    }, [autoPlay])

    useEffect(() => {
        if (currentSlide === totalSlides) {
            const timer = setTimeout(() => {
                setIsTransitioning(false)
                setCurrentSlide(0)
            }, 800)
            return () => clearTimeout(timer)
        }
    }, [currentSlide, totalSlides])


    const onDragStart = (clientX: number) => {
        isDragging.current = true
        dragStartX.current = clientX
        dragDeltaX.current = 0
        setIsTransitioning(false)
    }

    const onDragMove = (clientX: number) => {
        if (!isDragging.current) return
        dragDeltaX.current = clientX - dragStartX.current
        setLiveOffset(dragDeltaX.current)
    }

    const onDragEnd = () => {
        if (!isDragging.current) return
        isDragging.current = false

        const threshold = cardPx * 0.3  // 30% of card width to trigger slide change

        setIsTransitioning(true)
        setLiveOffset(0)

        if (dragDeltaX.current < -threshold) {
            // swiped left → next
            setCurrentSlide((prev) => Math.min(prev + 1, totalSlides))
        } else if (dragDeltaX.current > threshold) {
            // swiped right → prev
            setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1))
        }

        dragDeltaX.current = 0
    }

    // Mouse events
    const handleMouseDown = (e: React.MouseEvent) => onDragStart(e.clientX)
    const handleMouseMove = (e: React.MouseEvent) => onDragMove(e.clientX)
    const handleMouseUp = () => onDragEnd()
    const handleMouseLeave = () => { if (isDragging.current) onDragEnd() }

    // Touch events
    const handleTouchStart = (e: React.TouchEvent) => onDragStart(e.touches[0].clientX)
    const handleTouchMove = (e: React.TouchEvent) => onDragMove(e.touches[0].clientX)
    const handleTouchEnd = () => onDragEnd()

    const translateX = currentSlide * (cardPx + gap)

    return (
        <Box ref={containerRef} className='d-flex flex-column gap-1' sx={{ width: '100%',borderRadius:'50px', height: cardHeight, overflow: 'hidden' }}>
            <Box
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    display: 'flex',
                    gap: `${gap}px`,
                    flex: 1,
                    transform: `translateX(-${translateX}px)`,
                    transition: isTransitioning ? 'transform 0.8s ease-in-out' : 'none',
                    borderRadius:'50px'
                }}
            >
                {extendedSlides.map((slide: any, index) => (
                    <Box
                        key={index}
                        sx={{
                            minWidth: `${cardPx}px`,
                            height: "100%",
                            flexShrink: 0,
                            borderRadius:'50px',
                        }}
                    >
                        <Box  sx={{background:`url(${slide?.image_url})`, backgroundRepeat:'no-repeat', backgroundSize:'100% 100%', backgroundPosition:'center', borderRadius:'50px'}} className="h-100"></Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex',justifyContent: 'center', gap: 1, mt: 1 }}>
                {slides.map((_, index) => (
                    <Box
                        key={index}
                        onClick={() => {
                            setIsTransitioning(true)
                            setCurrentSlide(index)
                        }}
                        sx={{
                            width: 24,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'var(--secondary-color)',
                            opacity: currentSlide === index ? 1 : 0.4,
                            transition: 'opacity 0.3s ease',
                            cursor: 'pointer',
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

export default Carousel